import { useAuthStore } from "@/features/auth/stores/authStore"
import { useNavigate } from "react-router-dom"
import { ROUTES } from "@/shared/lib/constants"
import { cn } from "@/shared/lib/utils"
import { useState, useRef, useEffect } from "react"
import { Menu } from "lucide-react"
import { FarmSelector } from "@/features/shared/components/FarmSelector"

interface TopBarProps {
  onMenuClick: () => void
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const { user, clearAuth } = useAuthStore()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleLogout = () => {
    clearAuth()
    navigate(ROUTES.LOGIN)
  }

  return (
    <div className="flex h-16 items-center justify-between bg-white px-4 md:px-8 shadow-sm border-b" style={{ borderBottomColor: 'rgba(226, 232, 240, 0.3)' }}>
      {/* Left side - Hamburger + Farm Selector */}
      <div className="flex-1 flex items-center gap-3 md:gap-4">
        {/* Hamburger Menu - Mobile/Tablet only */}
        <button
          onClick={onMenuClick}
          className={cn(
            "lg:hidden p-2 rounded-xl transition-all duration-200",
            "hover:bg-slate-100 text-slate-600 hover:text-slate-900",
            "focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          )}
          aria-label="Open menu"
        >
          <Menu className="h-6 w-6" />
        </button>

        {/* Farm Selector - Hidden on mobile */}
        <div className="hidden md:block">
          <FarmSelector />
        </div>
      </div>

      {/* Right side - Notifications + User Menu */}
      <div className="flex items-center gap-3">
        {/* Notifications button */}
        <button
          className={cn(
            "font-roboto relative p-2.5 rounded-xl transition-all duration-200",
            "hover:bg-slate-100 text-slate-600 hover:text-slate-900",
            "focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          )}
        >
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
          {/* Notification dot */}
          <div className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-emerald-500 border-2 border-white"></div>
        </button>

        {/* Divider */}
        <div className="h-8 w-px bg-slate-200/50"></div>

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={cn(
              "font-roboto group flex items-center gap-3 rounded-xl px-3 py-2 transition-all duration-200",
              "hover:bg-slate-100",
              "focus:outline-none focus:ring-2 focus:ring-emerald-500/20",
              isMenuOpen && "bg-slate-100"
            )}
          >
            {/* Avatar with gradient */}
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-sm font-semibold text-white shadow-md shadow-emerald-500/20">
              {user?.fullName?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="text-left hidden sm:block">
              <div className="text-sm font-medium text-slate-900 tracking-tight">
                {user?.fullName}
              </div>
              <div className="text-xs text-slate-500 tracking-wide">
                Administrator
              </div>
            </div>
            {/* Chevron */}
            <svg
              className={cn(
                "h-4 w-4 text-slate-400 transition-transform duration-200",
                isMenuOpen && "rotate-180"
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Dropdown Menu - Premium Design */}
          {isMenuOpen && (
            <div className="absolute right-0 mt-2 w-64 origin-top-right animate-in z-50">
              <div className="rounded-xl bg-white shadow-xl overflow-hidden">
                {/* User info header */}
                <div className="px-4 py-3 bg-slate-50/50">
                  <div className="text-sm font-medium text-slate-900">
                    {user?.fullName}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {user?.email || 'admin@finharv.com'}
                  </div>
                </div>

                {/* Menu items */}
                <div className="p-1.5">
                  <button
                    onClick={() => {
                      setIsMenuOpen(false)
                      navigate(ROUTES.SETTINGS)
                    }}
                    className="font-roboto group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-700 transition-all duration-200 hover:bg-slate-100 hover:text-slate-900"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 group-hover:bg-emerald-100 group-hover:text-emerald-600 transition-colors duration-200">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium">Settings</div>
                      <div className="text-xs text-slate-500">Manage your account</div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setIsMenuOpen(false)
                      // Navigate to help or docs
                    }}
                    className="font-roboto group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-700 transition-all duration-200 hover:bg-slate-100 hover:text-slate-900"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors duration-200">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium">Help & Support</div>
                      <div className="text-xs text-slate-500">Get help with FinHarv</div>
                    </div>
                  </button>
                </div>

                {/* Logout button */}
                <div className="p-1.5">
                  <button
                    onClick={handleLogout}
                    className="font-roboto group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-red-600 transition-all duration-200 hover:bg-red-50"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-600 group-hover:bg-red-100 transition-colors duration-200">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium">Sign out</div>
                      <div className="text-xs text-red-500">Log out of your account</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
